import { TouchEvent, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { SxProps, Theme } from "@mui/material/styles";
import { ALL_PHOTOS, GalleryPhoto } from "../../shared";

// The photo's caption, e.g. "Kimberly Metcalfe - Médaille d'argent ceinture
// bleue (no-gi)". Used for both the visible title and the image alt text.
const photoLabel = (photo: GalleryPhoto) =>
  photo.type ? `${photo.text} (${photo.type})` : photo.text;

type PeekImageProps = {
  photo: GalleryPhoto;
  onClick: () => void;
  alt: string;
  sx?: SxProps<Theme>;
};

function PeekImage({ photo, onClick, alt, sx }: PeekImageProps) {
  return (
    <Box
      onClick={onClick}
      // Array form is how MUI merges an sx that arrives as a prop: it can be an
      // object, a callback or an array, so it can't just be spread.
      sx={[
        {
          cursor: "pointer",
          borderRadius: 2,
          overflow: "hidden",
          flexShrink: 0,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        component="img"
        src={photo.img}
        alt={alt}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          filter: "blur(3px) brightness(0.55)",
        }}
      />
    </Box>
  );
}

function PhotoCard({ photo, sx }: { photo: GalleryPhoto; sx?: SxProps<Theme> }) {
  return (
    <Card sx={sx}>
      <CardMedia
        component="img"
        image={photo.img}
        alt={photoLabel(photo)}
        sx={{ height: { xs: 300, md: 450 }, objectFit: "cover" }}
      />
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {photoLabel(photo)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {photo.competitionTitle} — {photo.date} — {photo.location}
        </Typography>
      </CardContent>
    </Card>
  );
}

export function GalerieCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const photo = ALL_PHOTOS[index];
  const prevPhoto = ALL_PHOTOS[(index - 1 + ALL_PHOTOS.length) % ALL_PHOTOS.length];
  const nextPhoto = ALL_PHOTOS[(index + 1) % ALL_PHOTOS.length];

  const goTo = (target: number) =>
    setIndex((target + ALL_PHOTOS.length) % ALL_PHOTOS.length);

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta > 50) goTo(index - 1);
    else if (delta < -50) goTo(index + 1);
    touchStartX.current = null;
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" py={4} px={2}>
      {/* Desktop: peek images either side of the card, arrows flanking everything */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          width: "100%",
          maxWidth: 900,
        }}
      >
        <IconButton onClick={() => goTo(index - 1)} aria-label="Photo précédente">
          <ArrowBackIosNewIcon />
        </IconButton>
        <PeekImage
          photo={prevPhoto}
          onClick={() => goTo(index - 1)}
          alt={`Photo précédente : ${photoLabel(prevPhoto)}`}
          sx={{ width: 130, height: 450 }}
        />
        <PhotoCard photo={photo} sx={{ width: "100%", maxWidth: 420 }} />
        <PeekImage
          photo={nextPhoto}
          onClick={() => goTo(index + 1)}
          alt={`Photo suivante : ${photoLabel(nextPhoto)}`}
          sx={{ width: 130, height: 450 }}
        />
        <IconButton onClick={() => goTo(index + 1)} aria-label="Photo suivante">
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>

      {/* Mobile: swipeable card, arrows below */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} sx={{ width: "100%" }}>
          <PhotoCard photo={photo} sx={{ width: "100%", maxWidth: 420, mx: "auto" }} />
        </Box>
        <Box display="flex" alignItems="center" gap={4} mt={2}>
          <IconButton size="small" onClick={() => goTo(index - 1)} aria-label="Photo précédente">
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => goTo(index + 1)} aria-label="Photo suivante">
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
